import { useEffect, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, LogOut, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LocaleToggle } from "@/components/shell/LocaleToggle";
import { signOut } from "@/lib/auth";
import { useOwnerIdentity } from "@/lib/useOwnerIdentity";

// Phase 6.12.2 - Minimal owner layout. Mirrors TenantLayout: spare header with
// the MapleKey mark, language toggle (limited to fully translated locales),
// Help, and sign-out. Mobile-first; 44px touch targets.
// On first load, applies the owner's preferred_locale ONCE, so a French owner
// provisioned in French sees French without touching the toggle. Subsequent
// manual toggling wins (per-device via localStorage, matching tenants).

const OWNER_LOCALES = ["en-CA", "fr-CA"] as const;

export function OwnerLayout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: owner } = useOwnerIdentity();
  const applied = useRef(false);

  useEffect(() => {
    if (applied.current) return;
    const loc = owner?.preferred_locale;
    if (loc && (loc === "en-CA" || loc === "fr-CA") && i18n.language !== loc) {
      i18n.changeLanguage(loc);
    }
    if (owner) applied.current = true;
  }, [owner, i18n]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-4">
          <div className="flex items-center gap-2 font-semibold">
            <Home className="h-5 w-5 text-primary" />
            <span>MapleKey</span>
          </div>
          <div className="flex items-center gap-1">
            <LocaleToggle locales={OWNER_LOCALES} />
            <Button
              variant="ghost"
              size="sm"
              className="h-11 min-w-11"
              aria-label={t("nav.help", "Help")}
            >
              <HelpCircle className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">{t("nav.help", "Help")}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="h-11 min-w-11"
              aria-label={t("auth.signOut", "Sign out")}
            >
              <LogOut className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">{t("auth.signOut", "Sign out")}</span>
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
